<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import { getAllProductTypes, getProductTypeById } from '@/modules/admin/products/product-types/api/productTypes.service'
import { getProductById, searchProducts } from '@/modules/admin/products/products/api/products.service'
import { imageImportActions, actionLabels, statusLabel } from '../lib/productImageImportDisplay'

const props = defineProps({
  visible: Boolean,
  entry: { type: Object, default: null },
  brandId: { type: Number, default: 0 },
  submitting: Boolean
})
const emit = defineEmits(['update:visible', 'save'])

const mode = ref('product')
const action = ref(imageImportActions.Add)
const productTypeId = ref(null)
const productId = ref(null)
const productTypes = ref([])
const productOptions = ref([])
const productSearch = ref('')
const loadingTypes = ref(false)
const loadingProducts = ref(false)
const loadingTarget = ref(false)
const targetImageCount = ref(null)
const targetStateValid = ref(true)
const targetError = ref('')

const canExecute = computed(() => !['InvalidFile', 'InvalidImage', 'Duplicate'].includes(props.entry?.status))
const actionOptions = computed(() => {
  if (!canExecute.value) return [{ label: actionLabels.Skip, value: imageImportActions.Skip }]
  const options = [{ label: actionLabels.Skip, value: imageImportActions.Skip }]
  const imageCount = targetImageCount.value
  if (targetStateValid.value && imageCount !== null) {
    options.unshift({ label: actionLabels.Add, value: imageImportActions.Add })
  }
  return options
})
const typeOptions = computed(() => productTypes.value
  .filter(item => Number(item.brandId) === Number(props.brandId))
  .map(item => ({ label: `${item.modelCode || 'Без ModelCode'} — ${item.name}`, value: item.id })))
const selectedProduct = computed(() => productOptions.value.find(item => item.id === productId.value))
const parsedCandidates = computed(() => {
  try { return props.entry?.candidatesJson ? JSON.parse(props.entry.candidatesJson) : [] } catch { return [] }
})

function reset() {
  mode.value = 'product'
  action.value = imageImportActions.Add
  productTypeId.value = props.entry?.productTypeId || null
  productId.value = props.entry?.productId || null
  productSearch.value = ''
  productOptions.value = []
  targetImageCount.value = props.entry?.productId ? props.entry.existingImageCount : null
  targetStateValid.value = true
  targetError.value = ''
}

function clearTarget() {
  targetImageCount.value = null
  targetStateValid.value = true
  targetError.value = ''
  action.value = imageImportActions.Skip
}

async function loadProductState(id) {
  if (!id) return
  loadingTarget.value = true
  targetError.value = ''
  try {
    const product = await getProductById(id)
    targetImageCount.value = Array.isArray(product?.images) ? product.images.length : 0
    targetStateValid.value = true
    action.value = imageImportActions.Add
  } catch (error) {
    targetImageCount.value = null
    targetStateValid.value = false
    targetError.value = error?.message || 'Не удалось получить состояние изображений товара.'
    action.value = imageImportActions.Skip
  } finally { loadingTarget.value = false }
}

async function loadProductTypeState(id) {
  if (!id) return
  loadingTarget.value = true
  targetError.value = ''
  try {
    const productType = await getProductTypeById(id)
    targetStateValid.value = productType.defaultProductCount === 1 && !!productType.defaultProductId
    targetImageCount.value = targetStateValid.value ? productType.defaultProductImageCount : null
    action.value = targetStateValid.value ? imageImportActions.Add : imageImportActions.Skip
    if (!targetStateValid.value) targetError.value = 'У ProductType нет ровно одного текущего default Product.'
  } catch (error) {
    targetImageCount.value = null
    targetStateValid.value = false
    targetError.value = error?.message || 'Не удалось получить состояние default Product.'
    action.value = imageImportActions.Skip
  } finally { loadingTarget.value = false }
}

async function loadTypes() {
  if (productTypes.value.length || loadingTypes.value) return
  loadingTypes.value = true
  try { productTypes.value = await getAllProductTypes() } finally { loadingTypes.value = false }
}

async function findProducts() {
  if (productSearch.value.trim().length < 2) return
  loadingProducts.value = true
  try { productOptions.value = await searchProducts(productSearch.value.trim(), 20) } finally { loadingProducts.value = false }
}

function save() {
  if (action.value !== imageImportActions.Skip && (!targetStateValid.value || targetImageCount.value === null || (mode.value === 'product' ? !productId.value : !productTypeId.value))) return
  emit('save', {
    action: action.value,
    productId: action.value === imageImportActions.Skip || mode.value !== 'product' ? null : productId.value,
    productTypeId: action.value === imageImportActions.Skip || mode.value !== 'product' ? productTypeId.value : null
  })
}

watch(() => props.visible, visible => { if (visible) { reset(); loadTypes() } })
watch(productId, value => { if (mode.value === 'product' && value) loadProductState(value) })
watch(productTypeId, value => { if (mode.value === 'productType' && value) loadProductTypeState(value) })
onMounted(loadTypes)
</script>

<template>
  <Dialog :visible="visible" modal :header="`Исправить: ${entry?.originalFileName || ''}`" :style="{ width: 'min(680px, 94vw)' }"
    @update:visible="emit('update:visible', $event)">
    <div v-if="entry" class="entry-dialog">
      <div class="entry-context">
        <Tag :value="statusLabel(entry.status)" severity="warn" />
        <span>{{ entry.errorMessage || 'Выберите актуальную цель и действие.' }}</span>
      </div>
      <div class="field">
        <label>Действие</label>
        <Dropdown v-model="action" :options="actionOptions" option-label="label" option-value="value" :disabled="submitting" />
      </div>
      <div v-if="canExecute && action !== imageImportActions.Skip" class="field">
        <label>Способ выбора цели</label>
        <div class="mode-buttons">
          <Button label="Конкретный товар" :outlined="mode !== 'product'" @click="mode = 'product'; productTypeId = null; clearTarget()" />
          <Button label="Тип товара" :outlined="mode !== 'productType'" @click="mode = 'productType'; productId = null; clearTarget()" />
        </div>
      </div>
      <div v-if="canExecute && action !== imageImportActions.Skip && mode === 'productType'" class="field">
        <label>ProductType текущего бренда</label>
        <Dropdown v-model="productTypeId" :options="typeOptions" option-label="label" option-value="value"
          placeholder="Выберите ProductType" :loading="loadingTypes" filter :disabled="submitting" />
        <small>Backend сам выберет текущий default Product.</small>
        <small v-if="targetError" class="target-error">{{ targetError }}</small>
        <small v-else-if="loadingTarget" class="target-info">Проверяем default Product...</small>
        <small v-else-if="targetImageCount !== null">Собственных изображений у default Product: {{ targetImageCount }}</small>
      </div>
      <div v-if="canExecute && action !== imageImportActions.Skip && mode === 'product'" class="field">
        <label>Поиск Product</label>
        <div class="search-row"><InputText v-model="productSearch" placeholder="Article или название ProductType" @keyup.enter="findProducts" />
          <Button icon="pi pi-search" :loading="loadingProducts" :disabled="productSearch.trim().length < 2" @click="findProducts" /></div>
        <small v-if="parsedCandidates.length">Кандидаты из backend: {{ parsedCandidates.length }}. Выбор всё равно проверяется backend.</small>
        <div v-if="productOptions.length" class="product-options">
          <button v-for="product in productOptions" :key="product.id" type="button" :class="{ selected: productId === product.id }" @click="productId = product.id">
            <strong>{{ product.articul || product.title }}</strong><span>{{ product.title }}</span>
          </button>
        </div>
        <div v-if="selectedProduct" class="selected-target">Выбран Product #{{ selectedProduct.id }}. Собственных изображений: {{ targetImageCount ?? 'проверка...' }}</div>
        <small v-if="targetError" class="target-error">{{ targetError }}</small>
        <small v-else-if="loadingTarget" class="target-info">Проверяем изображения Product...</small>
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="emit('update:visible', false)" />
      <Button label="Сохранить решение" icon="pi pi-check" :loading="submitting" :disabled="loadingTarget || (action !== imageImportActions.Skip && (!targetStateValid || targetImageCount === null || (mode === 'product' ? !productId : !productTypeId)))" @click="save" />
    </template>
  </Dialog>
</template>

<style scoped>
.entry-dialog { display: grid; gap: 18px; }.entry-context { display: flex; gap: 10px; align-items: center; color: #666; }.field { display: grid; gap: 7px; }.field > label { font-weight: 600; font-size: 13px; }.field small { color: #777; }.target-error { color: #b42318 !important; }.target-info { color: #795018 !important; }.mode-buttons, .search-row { display: flex; gap: 8px; }.search-row .p-inputtext { flex: 1; }.product-options { display: grid; gap: 6px; max-height: 220px; overflow: auto; }.product-options button { text-align: left; display: grid; gap: 3px; border: 1px solid #ddd; border-radius: 8px; padding: 9px 11px; background: white; cursor: pointer; }.product-options button.selected { border-color: #171717; background: #f3f3f0; }.product-options span { color: #777; font-size: 12px; }.selected-target { color: #247a45; font-weight: 600; }
</style>
